import { useEffect, useState } from "react";
import axios from "axios";
import { getCustomers, type Customer } from "../../services/customerService";
import api from "../../services/api";
import AddCustomerForm from "./AddCustomerForm";
import EditCustomerForm from "./EditCustomerForm";
import EntraConsentPrompt from "./EntraConsentPrompt";
import { useNavigate } from "react-router-dom";

/**
 * CustomerList component that displays a list of customers
 */
const CustomerList = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showConsentPrompt, setShowConsentPrompt] = useState(false);
  const [newCustomer, setNewCustomer] = useState<Customer | null>(null);
  const navigate = useNavigate();

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const data = await getCustomers();
      
      // Check if data is valid
      if (data && Array.isArray(data)) {
        setCustomers(data);
        setError(null);
      } else {
        console.error("Invalid customer data format:", data);
        setError("Received invalid customer data format from the API.");
      }
    } catch (err: unknown) {
      console.error("Error fetching customers:", err);
      // Provide more detailed error message
      if (axios.isAxiosError(err) && err.response) {
        setError(`Failed to load customers: ${err.response.status} ${err.response.statusText}. ${err.response.data?.error || ''}`);
      } else {
        setError("Failed to load customers. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleAddCustomer = () => {
    setShowAddForm(true);
  };

  const handleCustomerAdded = (customer: Customer) => {
    setShowAddForm(false);
    setNewCustomer(customer);
    setShowConsentPrompt(true);
    fetchCustomers();
  };
  
  // This function is used in the JSX below
  const handleConsentClosed = () => {
    setShowConsentPrompt(false);
    setNewCustomer(null);
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
  };

  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowEditForm(true);
  };

  const handleCancelEdit = () => {
    setSelectedCustomer(null);
    setShowEditForm(false);
  };

  const handleCustomerUpdated = () => {
    setSelectedCustomer(null);
    setShowEditForm(false);
    fetchCustomers();
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Filter customers based on search term
  const filteredCustomers = customers.filter(customer => {
    const searchTermLower = searchTerm.toLowerCase();
    
    // Check customer name
    const nameMatch = customer.customerName.toLowerCase().includes(searchTermLower);
    
    // Check customer number (convert to string if needed)
    const customerNumberStr = String(customer.customerNumber);
    const numberMatch = customerNumberStr.toLowerCase().includes(searchTermLower);
    
    // Check email if it exists
    const emailMatch = customer.primaryContactEmail ? 
      customer.primaryContactEmail.toLowerCase().includes(searchTermLower) : 
      false;
    
    return nameMatch || numberMatch || emailMatch;
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-lg">
        <div className="spinner"></div>
        <p className="mt-md">Loading customers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error p-md" style={{ borderRadius: 'var(--border-radius-md)' }}>
        <h2 className="text-white mb-sm" style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'bold' }}>Error!</h2>
        <p className="text-white mb-md">{error}</p>
        <div className="mt-md">
          <h3 className="text-white mb-sm" style={{ fontSize: 'var(--font-size-md)', fontWeight: 'bold' }}>Debugging Information:</h3>
          <p className="text-white mb-xs">Please open the browser console (F12) to see detailed error information.</p>
          <p className="text-white mb-xs">You can also try the following:</p>
          <ul className="ml-md" style={{ color: 'white' }}>
            <li>Check if you're properly authenticated</li>
            <li>Verify that the API endpoint is accessible</li>
            <li>Check network tab in developer tools for API response</li>
          </ul>
        </div>
      </div>
    );
  }

  if (customers.length === 0 && !showAddForm) {
    return (
      <div>
        <button 
          className="btn btn-primary mb-md"
          onClick={handleAddCustomer}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Add Customer
        </button>
        
        <div className="bg-info p-md" style={{ borderRadius: 'var(--border-radius-md)' }}>
          <h2 className="text-white mb-sm" style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'bold' }}>No customers found</h2>
          <p className="text-white">There are no customers in the system yet. Click the "Add Customer" button to create one.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {showConsentPrompt ? (
        <EntraConsentPrompt 
          customerName={(newCustomer || selectedCustomer)?.customerName || ''}
          onClose={() => {
            setShowConsentPrompt(false);
            setNewCustomer(null);
            setSelectedCustomer(null);
          }}
        />
      ) : showEditForm && selectedCustomer ? (
        <EditCustomerForm 
          customer={selectedCustomer}
          onCustomerUpdated={handleCustomerUpdated}
          onCancel={handleCancelEdit}
        />
      ) : showAddForm ? (
        <AddCustomerForm 
          onCustomerAdded={handleCustomerAdded} 
          onCancel={handleCancelAdd} 
        />
      ) : (
        <>
          <div className="flex justify-between items-center mb-lg">
            <div className="flex items-center">
              <input 
                type="text" 
                placeholder="Search customers..." 
                className="p-sm"
                value={searchTerm}
                onChange={handleSearch}
                style={{ 
                  width: '250px',
                  borderRadius: 'var(--border-radius-sm)'
                }}
              />
            </div>
            <button 
              className="btn btn-primary"
              onClick={handleAddCustomer}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add Customer
            </button>
          </div>
          
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Customer Number</th>
                  <th>Name</th>
                  <th>Primary Contact</th>
                  <th>Entra Tenant ID</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center p-md">
                      No customers match your search criteria
                    </td>
                  </tr>
                ) : (
                  filteredCustomers.map((customer) => (
                    <tr key={customer.customerId}>
                      <td>{customer.customerNumber}</td>
                      <td>{customer.customerName}</td>
                      <td>{customer.primaryContactEmail || "-"}</td>
                      <td>{customer.entraTenantId || "-"}</td>
                      <td>
                        <div className="flex gap-sm">
                          {/* Edit Customer Button */}
                          <button 
                            className="btn btn-text"
                            onClick={() => handleEditCustomer(customer)}
                            title="Edit Customer"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </button>
                          
                          {/* Request Entra Consent Button */}
                          <button 
                            className="btn btn-text"
                            onClick={() => {
                              setSelectedCustomer(customer);
                              setShowConsentPrompt(true);
                            }}
                            title="Request Entra Consent"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                            </svg>
                          </button>
                          
                          {/* Prep Tenant Button */}
                          {customer.entraTenantId && (
                            <button 
                              className="btn btn-text"
                              onClick={async () => {
                                try {
                                  setLoading(true);
                                  await api.post(`/customer/${customer.customerId}/prep-tenant`);
                                  fetchCustomers();
                                } catch (err) {
                                  console.error("Error prepping tenant:", err);
                                  alert("Failed to prep tenant. Please try again.");
                                } finally {
                                  setLoading(false);
                                }
                              }}
                              disabled={Boolean(customer.tenantPrepDate && customer.tenantPrepDate !== '')}
                              title={customer.tenantPrepDate && customer.tenantPrepDate !== '' ? "Tenant Already Prepped" : "Prep Tenant"}
                              style={{ 
                                color: customer.tenantPrepDate && customer.tenantPrepDate !== '' ? 'var(--color-text-disabled)' : 'var(--color-primary)'
                              }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                              </svg>
                            </button>
                          )}
                          
                          {/* Manage Tenant Groups Button */}
                          {customer.entraTenantId && (
                            <button 
                              className="btn btn-text"
                              onClick={() => navigate(`/customer/${customer.customerId}/tenant-groups`)}
                              disabled={!customer.tenantPrepDate}
                              title={!customer.tenantPrepDate ? "Tenant must be prepped before managing groups" : "Manage Tenant Groups"}
                              style={{ 
                                color: !customer.tenantPrepDate ? 'var(--color-text-disabled)' : 'var(--color-primary)'
                              }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                              </svg>
                            </button>
                          )}
                          
                          {/* Delete Customer Button */}
                          <button 
                            className="btn btn-text"
                            onClick={async () => {
                              if (window.confirm(`Are you sure you want to delete ${customer.customerName}? This action cannot be undone.`)) {
                                try {
                                  await api.delete(`/customer/${customer.customerId}`);
                                  fetchCustomers();
                                } catch (err) {
                                  console.error("Error deleting customer:", err);
                                  alert("Failed to delete customer. Please try again.");
                                }
                              }
                            }}
                            title="Delete Customer"
                            style={{ color: 'var(--color-error)' }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6h18"></path>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerList;
